<template>
  <b-row class="card-list">
    <b-col cols="12">
      <!-- generate loading components -->
      <component :is="`${loadingComponent}HeaderLoading`">
        <template #default>
          <div
            class="card-list__item"
            v-for="(item, index) in reGeneratedItems"
            :key="index"
          >
            <fade-transition :delay="300">
              <span v-if="!item.deleted">
                <div
                  class="card-list__header"
                  :class="{
                    'card-list__header--disabled': item.disabled,
                    'cursor--auto': !hasBody,
                    active: item.selected,
                  }"
                  v-b-toggle="`card-${index}`"
                >
                  <slot name="header" :item="item" />
                </div>
                <b-collapse
                  v-if="hasBody"
                  :accordion="accordion"
                  :id="`card-${index}`"
                  @show="openBody(item)"
                >
                  <div class="card-list__body">
                    <BodyLoading
                      :loading="includes(item[uniqueKey], bodyLoading)"
                    >
                      <template #default>
                        <slot name="body" :item="item" />
                        <slot name="card-body" :item="getCachedData(item)" />
                      </template>
                    </BodyLoading>
                  </div>
                </b-collapse>
              </span>
            </fade-transition>
          </div>
        </template>
      </component>
    </b-col>
  </b-row>
</template>

<script>
import {
  ParvandehHeaderLoading,
  ParvandehBodyLoading,
  TermHeaderLoading,
  TermBodyLoading,
  CenterExecuteHeaderLoading,
  CenterExecuteBodyLoading,
  CenterExecuteCasesHeaderLoading,
  CenterExecuteCasesBodyLoading,
  CenterExecuteAccessHeaderLoading,
  CenterExecuteAccessBodyLoading,
  CompleteInfoHeaderLoading,
  CompleteInfoBodyLoading,
  BodyLoading,
} from "./card-loading";
import { computed, provide, ref, toRefs, watch } from "vue";
import { pathOr, includes } from "ramda";
import { generateError } from "@/shared/utils";

export default {
  components: {
    ParvandehHeaderLoading,
    ParvandehBodyLoading,
    TermHeaderLoading,
    TermBodyLoading,
    CenterExecuteHeaderLoading,
    CenterExecuteBodyLoading,
    CenterExecuteCasesHeaderLoading,
    CenterExecuteCasesBodyLoading,
    CenterExecuteAccessHeaderLoading,
    CenterExecuteAccessBodyLoading,
    CompleteInfoHeaderLoading,
    CompleteInfoBodyLoading,
    BodyLoading,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    deletedItemsId: {
      type: Array,
      default: () => [],
    },
    loadingComponent: {
      type: String,
      default: "",
    },
    showBody: {
      type: Function,
      default: () => {},
    },
    accordion: {
      type: String,
      default: "card-accordion",
    },
    hasBody: {
      type: Boolean,
      default: true,
    },
    uniqueKey: {
      type: String,
      default: "id",
    },
  },
  setup(props) {
    // generate props data
    const { items, deletedItemsId, loadingComponent, accordion } =
      toRefs(props);

    const reGeneratedItems = computed(() => {
      return items.value.map((item) => {
        const id = pathOr(null, ["id"], item);
        const code = pathOr(null, ["code"], item);
        const deleted =
          includes(id, deletedItemsId.value) ||
          includes(code, deletedItemsId.value);
        return { ...item, deleted, bodyData: null };
      });
    });

    // open card body and call async service for get body data
    const bodyLoading = ref([]);
    const bodyDataCached = ref([]);

    const getCachedData = (item) => {
      return bodyDataCached.value.find(
        (row) => row[props.uniqueKey] === item[props.uniqueKey]
      );
    };

    const openBody = async (item) => {
      // check exist in cache data
      if (getCachedData(item)) return;
      const uniqueKey = pathOr(null, [props.uniqueKey], item);
      try {
        bodyLoading.value.push(uniqueKey);
        const response = await props.showBody(item);

        // add response with unique key to cache data array
        bodyDataCached.value.push({ ...response });
      } catch (error) {
        generateError(error);
      } finally {
        const index = bodyLoading.value.findIndex((item) => item === uniqueKey);
        if (index !== -1) {
          bodyLoading.value.splice(index, 1);
        }
      }
    };

    // generate provide data to child components
    provide(
      "BODY_LOADING",
      computed(() => bodyLoading.value)
    );

    // return data to be used in template
    return {
      reGeneratedItems,
      loadingComponent,
      accordion,
      openBody,
      includes,
      bodyLoading,
      bodyDataCached,
      getCachedData,
    };
  },
};
</script>
